import { FC, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { IonCheckbox, IonItem, IonLabel, IonSpinner } from '@ionic/react'

import { Button } from '@components/atoms/Button'
import { InputField } from '@components/atoms/InputField'
import { RequiredMark } from '@components/atoms/RequiredMark'

interface ICustomerRewardsForm {
  onSubmit: (data: unknown) => Promise<void>
  isLoading: boolean
}

export const CustomRewardsForm: FC<ICustomerRewardsForm> = ({
  onSubmit,
  isLoading,
}) => {
  const { t } = useTranslation('common')

  const defaultValues = {
    usernames: '',
    activityMessage: '',
    reward: '0',
    contestId: '',
    postMessage: '',
    publishPost: false,
  }

  const {
    control,
    handleSubmit,
    formState: { isValid },
    watch,
    trigger,
    reset,
  } = useForm({
    defaultValues,
    mode: 'onChange',
  })

  const watchPublishPost = watch('publishPost', defaultValues.publishPost)

  useEffect(() => {
    trigger(['publishPost'])
  }, [trigger, watchPublishPost])

  return (
    <form className="mb-8">
      <div className="mb-8">
        <Controller
          control={control}
          name="usernames"
          rules={{ minLength: 2, required: true }}
          render={({ field }) => (
            <>
              <label className="flex flex-col">
                <div className="pl-4 pb-2">
                  <Trans i18nKey="customRewards.form.usernames">
                    {t('customRewards.form.usernames')}
                  </Trans>
                  <RequiredMark />
                </div>
                <textarea
                  className="bg-zinc-700"
                  rows={4}
                  cols={50}
                  onChange={(value) => field.onChange(value)}
                  value={field.value}
                />
              </label>
              <div className="pt-2 text-xs text-gray-400">
                {t('customRewards.form.usernamesInfo')}
              </div>
            </>
          )}
        />
      </div>
      <div className="mb-8">
        <Controller
          control={control}
          name="activityMessage"
          rules={{ minLength: 2, required: true }}
          render={({ field }) => (
            <label className="flex flex-col">
              <div className="pl-4 pb-2">
                <Trans i18nKey="customRewards.form.activityMessage">
                  {t('customRewards.form.activityMessage')}
                </Trans>
                <RequiredMark />
              </div>
              <textarea
                className="bg-zinc-700"
                rows={4}
                cols={50}
                onChange={(value) => field.onChange(value)}
                value={field.value}
              />
            </label>
          )}
        />
      </div>
      <div className="mb-8">
        <Controller
          control={control}
          name="reward"
          rules={{ required: true, minLength: 1 }}
          render={({ field }) => (
            <InputField
              textAlign="right"
              label={t('customRewards.form.reward')}
              value={field.value}
              role="presentation"
              autocomplete="off"
              type="number"
              labelSuffix={<RequiredMark />}
              onChange={(e) => field.onChange(e.value)}
              isDefault
            />
          )}
        />
      </div>
      <div className="mb-2">
        <Controller
          control={control}
          name="publishPost"
          render={({ field }) => (
            <IonItem>
              <IonLabel>
                <Trans i18nKey="customRewards.form.publishPost">
                  {t('customRewards.form.publishPost')}
                </Trans>
              </IonLabel>
              <IonCheckbox
                checked={Boolean(field.value)}
                onIonChange={async (e) => {
                  field.onChange(e.detail.checked)
                }}
              />
            </IonItem>
          )}
        />
      </div>
      {watchPublishPost && (
        <>
          <div className="mb-8">
            <Controller
              control={control}
              name="contestId"
              rules={{ minLength: 2, required: watchPublishPost }}
              render={({ field }) => (
                <InputField
                  textAlign="right"
                  label={t('customRewards.form.contestId')}
                  value={field.value}
                  role="presentation"
                  autocomplete="off"
                  type="text"
                  labelSuffix={<RequiredMark />}
                  onChange={(e) => field.onChange(e.value)}
                  isDefault
                />
              )}
            />
          </div>
          <div className="mb-8">
            <Controller
              control={control}
              name="postMessage"
              rules={{ minLength: 2, required: watchPublishPost }}
              render={({ field }) => (
                <label className="flex flex-col">
                  <div className="pl-4 pb-2">
                    <Trans i18nKey="customRewards.form.postMessage">
                      {t('customRewards.form.postMessage')}
                    </Trans>
                    <RequiredMark />
                  </div>
                  <textarea
                    className="bg-zinc-700"
                    rows={4}
                    cols={50}
                    onChange={(value) => field.onChange(value)}
                    value={field.value}
                  />
                </label>
              )}
            />
          </div>
        </>
      )}
      <div className="text-center">
        <Button
          isDisabled={!isValid || isLoading}
          onClick={handleSubmit(async (data) => {
            await onSubmit(data)
            reset()
          })}
        >
          {!isLoading ? (
            t('publish')
          ) : (
            <IonSpinner
              name="dots"
              slot="icon-only"
              className={'text-quaternary'}
            />
          )}
        </Button>
      </div>
    </form>
  )
}
